import { computed } from 'vue'
import i18n from '../locales'

const STORAGE_KEY = 'nm_locale'

export interface LanguageOption {
  code: string
  nativeName: string
}

export const availableLanguages: LanguageOption[] = [
  { code: 'zh-CN', nativeName: '简体中文' },
  { code: 'en', nativeName: 'English' },
  { code: 'fr', nativeName: 'Français' },
  { code: 'es', nativeName: 'Español' },
  { code: 'it', nativeName: 'Italiano' },
  { code: 'pt', nativeName: 'Português' },
  { code: 'nl', nativeName: 'Nederlands' },
  { code: 'ru', nativeName: 'Русский' },
  { code: 'pl', nativeName: 'Polski' },
  { code: 'sv', nativeName: 'Svenska' },
]

const localeToLangCode: Record<string, string> = {
  zh: 'zh-CN',
  en: 'en',
  fr: 'fr',
  es: 'es',
  it: 'it',
  pt: 'pt',
  nl: 'nl',
  ru: 'ru',
  pl: 'pl',
  sv: 'sv',
}

async function detectLocaleByIP(): Promise<string | null> {
  try {
    const res = await fetch('/api/v1/timezone/current')
    if (!res.ok) return null
    const data = await res.json()
    const json = data as { locale?: string; country?: string }
    if (json.locale && localeToLangCode[json.locale]) {
      return localeToLangCode[json.locale]
    }
    if (json.country) {
      const countryToLocale: Record<string, string> = {
        CN: 'zh-CN', TW: 'zh-CN', HK: 'zh-CN',
        US: 'en', GB: 'en', AU: 'en', CA: 'en', NZ: 'en', IE: 'en',
        FR: 'fr', BE: 'fr',
        ES: 'es', MX: 'es', AR: 'es', CO: 'es',
        IT: 'it',
        PT: 'pt', BR: 'pt',
        NL: 'nl',
        RU: 'ru',
        PL: 'pl',
        SE: 'sv',
      }
      return countryToLocale[json.country] || null
    }
    return null
  } catch {
    return null
  }
}

function detectLocaleByBrowser(): string {
  if (typeof navigator === 'undefined') return 'zh-CN'
  const browserLang = (navigator.language || '').toLowerCase()
  const prefix = browserLang.split('-')[0]
  return localeToLangCode[prefix] || 'zh-CN'
}

export function useLang() {
  const currentLocale = computed(() => i18n.global.locale.value)

  const currentLanguage = computed(() =>
    availableLanguages.find(l => l.code === currentLocale.value) || availableLanguages[0],
  )

  async function setLocale(code: string): Promise<void> {
    const supported = availableLanguages.some(l => l.code === code)
    if (!supported) return
    i18n.global.locale.value = code as any
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, code)
    }
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', code)
    }
  }

  async function autoDetect(): Promise<void> {
    if (typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY)) return

    const ipLocale = await detectLocaleByIP()
    if (ipLocale) {
      await setLocale(ipLocale)
      return
    }

    const browserLocale = detectLocaleByBrowser()
    await setLocale(browserLocale)
  }

  return {
    currentLocale,
    currentLanguage,
    availableLanguages,
    setLocale,
    autoDetect,
  }
}
